import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Reusable phone input with country code picker. Stores the full number
 * (dial code + local number, e.g. "+33612345678") in the parent via onChange.
 * Accepts an existing value (any format) and parses it back best-effort.
 */
const COUNTRIES = [
  { code: "FR", dial: "+33", name: "France", flag: "🇫🇷" },
  { code: "BE", dial: "+32", name: "Belgique", flag: "🇧🇪" },
  { code: "CH", dial: "+41", name: "Suisse", flag: "🇨🇭" },
  { code: "LU", dial: "+352", name: "Luxembourg", flag: "🇱🇺" },
  { code: "MC", dial: "+377", name: "Monaco", flag: "🇲🇨" },
  { code: "GB", dial: "+44", name: "Royaume-Uni", flag: "🇬🇧" },
  { code: "IE", dial: "+353", name: "Irlande", flag: "🇮🇪" },
  { code: "ES", dial: "+34", name: "Espagne", flag: "🇪🇸" },
  { code: "PT", dial: "+351", name: "Portugal", flag: "🇵🇹" },
  { code: "IT", dial: "+39", name: "Italie", flag: "🇮🇹" },
  { code: "DE", dial: "+49", name: "Allemagne", flag: "🇩🇪" },
  { code: "NL", dial: "+31", name: "Pays-Bas", flag: "🇳🇱" },
  { code: "US", dial: "+1", name: "USA / Canada", flag: "🇺🇸" },
  { code: "MA", dial: "+212", name: "Maroc", flag: "🇲🇦" },
  { code: "DZ", dial: "+213", name: "Algérie", flag: "🇩🇿" },
  { code: "TN", dial: "+216", name: "Tunisie", flag: "🇹🇳" },
  { code: "AE", dial: "+971", name: "Émirats arabes unis", flag: "🇦🇪" },
  { code: "SA", dial: "+966", name: "Arabie saoudite", flag: "🇸🇦" },
  { code: "QA", dial: "+974", name: "Qatar", flag: "🇶🇦" },
  { code: "RU", dial: "+7", name: "Russie", flag: "🇷🇺" },
  { code: "CN", dial: "+86", name: "Chine", flag: "🇨🇳" },
  { code: "JP", dial: "+81", name: "Japon", flag: "🇯🇵" },
  { code: "AU", dial: "+61", name: "Australie", flag: "🇦🇺" },
  { code: "BR", dial: "+55", name: "Brésil", flag: "🇧🇷" },
];

// Sort dials by length desc so "+352" matches before "+3"
const DIALS_SORTED = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

function parseValue(value) {
  if (!value) return { dial: "+33", local: "" };
  const raw = String(value).replace(/\s+/g, "");
  const match = DIALS_SORTED.find((c) => raw.startsWith(c.dial));
  if (match) return { dial: match.dial, local: raw.slice(match.dial.length) };
  if (raw.startsWith("+")) return { dial: "+33", local: raw };
  if (raw.startsWith("0")) return { dial: "+33", local: raw.slice(1) };
  return { dial: "+33", local: raw };
}

export default function PhoneInput({ value, onChange, placeholder = "6 12 34 56 78", "data-testid": testId }) {
  const initial = useMemo(() => parseValue(value), [value]);
  const [dial, setDial] = useState(initial.dial);
  const [local, setLocal] = useState(initial.local);

  const emit = (nextDial, nextLocal) => {
    const cleaned = String(nextLocal).replace(/[^\d]/g, "");
    onChange(`${nextDial}${cleaned}`);
  };

  const current = COUNTRIES.find((c) => c.dial === dial) || COUNTRIES[0];

  return (
    <div className="flex gap-2">
      <Select value={dial} onValueChange={(v) => { setDial(v); emit(v, local); }}>
        <SelectTrigger className="w-[112px] flex-none" data-testid={testId ? `${testId}-dial` : "phone-dial"}>
          <SelectValue>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-base leading-none">{current.flag}</span>
              <span className="tabular-nums">{current.dial}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[320px]">
          {COUNTRIES.map((c) => (
            <SelectItem key={c.code} value={c.dial}>
              <span className="inline-flex items-center gap-2">
                <span className="text-base leading-none">{c.flag}</span>
                <span className="tabular-nums text-muted-foreground">{c.dial}</span>
                <span className="text-sm">{c.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        inputMode="tel"
        value={local}
        onChange={(e) => { setLocal(e.target.value); emit(dial, e.target.value); }}
        placeholder={placeholder}
        data-testid={testId}
        className="flex-1"
      />
    </div>
  );
}
